import {
  can,
  canAll,
  canAny,
  type Permission,
  type RelationshipResource,
  type RelationshipUser,
  roleHasPermission,
} from "@blis/shared/permissions";

import { useAuth } from "@/features/auth/hooks";

function toRelationshipUser(user: {
  role: string;
  profile: { org: { id: number }; allowedOrgs: number[] };
}): RelationshipUser {
  return {
    role: user.role,
    org: user.profile.org.id,
    allowedOrgs: user.profile.allowedOrgs ?? [],
  };
}

/**
 * Client-side permission hook.
 *
 * Without a resource: checks if the role has the permission in ANY relationship.
 * Use this for UI visibility (sidebar links, top-level nav).
 *
 * With a resource: checks the specific relationship between user and resource.
 * Use this for per-row action buttons (validate, report, etc.).
 */
export function usePermissions(resource?: RelationshipResource) {
  const { user } = useAuth();
  const relUser = toRelationshipUser(user);

  if (!resource) {
    return {
      can: (permission: Permission) =>
        roleHasPermission(relUser.role, permission),
      canAny: (permissions: Permission[]) =>
        permissions.some((p) => roleHasPermission(relUser.role, p)),
      canAll: (permissions: Permission[]) =>
        permissions.every((p) => roleHasPermission(relUser.role, p)),
    };
  }

  return {
    can: (permission: Permission) => can(relUser, permission, resource),
    canAny: (permissions: Permission[]) =>
      canAny(relUser, permissions, resource),
    canAll: (permissions: Permission[]) =>
      canAll(relUser, permissions, resource),
  };
}
